'use client';

import { useEffect, useState } from 'react';

const CLAVE = 'siniestrados.cookies';
export interface PreferenciasCookies {
  necesarias: true;
  analisis: boolean;
  publicidad: boolean;
  en: string;
}

export function leerPreferencias(): PreferenciasCookies | null {
  try {
    const v = window.localStorage.getItem(CLAVE);
    return v ? (JSON.parse(v) as PreferenciasCookies) : null;
  } catch {
    return null;
  }
}

function guardar(p: Omit<PreferenciasCookies, 'necesarias' | 'en'>) {
  try {
    window.localStorage.setItem(
      CLAVE,
      JSON.stringify({ necesarias: true, ...p, en: new Date().toISOString() }),
    );
  } catch {
    // Navegador sin almacenamiento: el aviso vuelve a salir, sin activar nada opcional.
  }
}

/**
 * Aviso de cookies (arquitectura 2.12): por omisión solo las necesarias; análisis y publicidad
 * se activan únicamente si la persona acepta. La elección se cambia desde el pie de página.
 */
export function AvisoCookies({
  politica = '/cookies',
  servidor = false,
}: {
  politica?: string;
  /** Vitrina con SSR y SCRIPT_COOKIES en el <head>: el aviso sale desde el HTML del servidor. */
  servidor?: boolean;
}) {
  const [abierto, setAbierto] = useState(servidor);
  const [detalle, setDetalle] = useState(false);
  const [analisis, setAnalisis] = useState(false);
  const [publicidad, setPublicidad] = useState(false);

  useEffect(() => {
    const p = leerPreferencias();
    if (p) {
      setAnalisis(p.analisis);
      setPublicidad(p.publicidad);
      setAbierto(false);
    } else setAbierto(true);
    const reabrir = () => {
      delete document.documentElement.dataset.cookies;
      setDetalle(true);
      setAbierto(true);
    };
    window.addEventListener('siniestrados:cookies', reabrir);
    return () => window.removeEventListener('siniestrados:cookies', reabrir);
  }, []);

  if (!abierto) return null;
  const cerrar = (a: boolean, p: boolean) => {
    guardar({ analisis: a, publicidad: p });
    setAbierto(false);
  };
  return (
    <div className="aviso-cookies" role="dialog" aria-label="Preferencias de cookies">
      <p>
        Usamos cookies necesarias para la sesión y el carrito. Las de análisis y publicidad solo se
        activan si las aceptas. <a href={politica}>Política de cookies</a>
      </p>
      {detalle && (
        <div className="opciones-cookies">
          <label>
            <input type="checkbox" checked disabled /> Necesarias (siempre activas)
          </label>
          <label>
            <input
              type="checkbox"
              checked={analisis}
              onChange={(e) => setAnalisis(e.target.checked)}
            />{' '}
            Análisis
          </label>
          <label>
            <input
              type="checkbox"
              checked={publicidad}
              onChange={(e) => setPublicidad(e.target.checked)}
            />{' '}
            Publicidad
          </label>
        </div>
      )}
      <div className="acciones-cookies">
        <button className="boton" onClick={() => cerrar(false, false)}>
          Solo necesarias
        </button>
        {detalle ? (
          <button className="boton" onClick={() => cerrar(analisis, publicidad)}>
            Guardar mi elección
          </button>
        ) : (
          <button className="boton" onClick={() => setDetalle(true)}>
            Elegir
          </button>
        )}
        <button className="boton primario" onClick={() => cerrar(true, true)}>
          Aceptar todas
        </button>
      </div>
    </div>
  );
}

/** Enlace del pie para volver a abrir las preferencias. */
export function PreferenciasCookiesEnlace() {
  return (
    <button
      className="enlace"
      onClick={() => window.dispatchEvent(new Event('siniestrados:cookies'))}
    >
      Preferencias de cookies
    </button>
  );
}
